import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { uploadFileOffline } from "@/lib/uploadOffline";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import UploadDropzone from "@/components/UploadDropzone";
import { File as FileIcon, FileText, FileSpreadsheet, Image as ImageIcon, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Project Documents tab — internal reference library the field crew
 * reads on-site (SOWs, permits, drawings, notes). Deliberately kept
 * separate from Test Docs (which drive closeout AI extraction) and
 * Photos (which drive photo-template completion). Project docs are
 * NEVER included in the closeout PDF or the raw customer ZIP.
 *
 * Roles:
 * - Upload: admin, pm, foreman.
 * - Delete: admin, pm (foreman can't delete so a permit doesn't get
 *   accidentally purged mid-job).
 * - Read: anyone who can view the project.
 */
export default function ProjectDocsPanel({ project, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const canUpload = ["admin", "pm", "foreman"].includes(user?.role);
  const canDelete = ["admin", "pm"].includes(user?.role);

  const items = project.project_docs || [];

  const upload = async (files) => {
    setBusy(true);
    try {
      let queued = 0, uploaded = 0;
      for (const file of files) {
        const r = await uploadFileOffline({
          projectId: project.id,
          file,
          fields: { kind: "project_doc", category: "Project Documents", notes },
          timeout: 180_000,
        });
        if (r.queued) queued += 1; else uploaded += 1;
      }
      if (uploaded > 0 && queued === 0) toast.success(`Uploaded ${uploaded} document(s)`);
      else if (queued > 0 && uploaded === 0) toast.info(`${queued} queued — will upload when back online`);
      else toast.success(`${uploaded} uploaded, ${queued} queued`);
      setNotes("");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("Document removed");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const iconFor = (f) => {
    if (f.content_type?.startsWith("image/")) return <ImageIcon className="w-7 h-7 text-sky-400" />;
    if (f.content_type === "application/pdf") return <FileText className="w-7 h-7 text-red-400" />;
    if (/\.(xlsx|xls|csv)$/i.test(f.original_filename || "")) return <FileSpreadsheet className="w-7 h-7 text-emerald-400" />;
    return <FileIcon className="w-7 h-7 text-slate-400" />;
  };

  return (
    <div className="space-y-4" data-testid="project-docs-panel">
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <div>
            <div className="font-display font-bold text-lg">Project Documents</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Field-reference material for the crew on-site (SOWs, permits, drawings, notes).
              Not attached to the customer closeout package.
            </div>
          </div>
          {canUpload ? (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-display font-bold">
                  Notes (optional)
                </label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Approved SOW, revision 3"
                  className="bg-slate-950 border-slate-700 mt-1"
                  data-testid="project-docs-notes"
                />
              </div>
              <UploadDropzone
                onFiles={upload}
                busy={busy}
                accept="*/*"
                label="Drop project documents or click to select"
                hint="Any file type. Max ~50 MB per file recommended."
                testid="project-docs-dropzone"
              />
            </>
          ) : (
            <div className="text-xs text-slate-500 italic">
              You don't have permission to upload project documents.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="project-docs-list">
        {items.length === 0 && (
          <div className="text-sm text-slate-400 col-span-full">
            No project documents uploaded yet.
          </div>
        )}
        {items.map((f) => (
          <Card key={f.id} className="bg-slate-900 border-slate-800" data-testid={`project-doc-${f.id}`}>
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded bg-slate-950 border border-slate-800 flex items-center justify-center shrink-0 overflow-hidden">
                  {f.content_type?.startsWith("image/") ? (
                    <img
                      src={fileUrl(f.id, { size: "thumb" })}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    iconFor(f)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" title={f.original_filename}>
                    {f.original_filename}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {new Date(f.uploaded_at).toLocaleString()}
                    {f.size ? ` · ${(f.size / 1024).toFixed(0)} KB` : ""}
                  </div>
                  {f.notes && (
                    <div className="text-[11px] text-slate-400 mt-1 italic truncate" title={f.notes}>
                      {f.notes}
                    </div>
                  )}
                  <div className="flex gap-3 mt-2">
                    <a
                      href={fileUrl(f.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1"
                      data-testid={`project-doc-open-${f.id}`}
                    >
                      <Download className="w-3 h-3" /> Open
                    </a>
                    {canDelete && (
                      <button
                        onClick={() => remove(f.id, f.original_filename)}
                        className="text-xs text-red-400 hover:underline inline-flex items-center gap-1"
                        data-testid={`project-doc-delete-${f.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
